import * as React from 'react';
import { useTranslation } from 'next-i18next';
import tw, { styled, theme } from 'twin.macro';
import { useIntercom } from 'react-use-intercom';

import { ButtonV2 } from '@restworld/ui-ds';
import { HeadphoneIcon, useAnalytics, useIsFreemiumUser } from '@restworld/utils-common';
import { useAuth } from '@restworld/feature-authentication';

import { EmployerPageContentWrapper } from '../employer-page-content-wrapper';
import { EmployerPrimaryLayoutHeader, EmployerSecondaryLayout } from '../../layouts';
import {
  ActivateFirstSlotBanner,
  AdvantagesSection,
  ContactSection,
  FaqSection,
  FlexibleSlotSection,
  FunctionalitiesSection,
  HeaderSection,
  HowItWorksSection,
  HowItWorksStepsSection,
  IncludedFeaturesSection,
  TrustedCompaniesSection,
} from './product-description.components';
import {
  FlexibleSlotPurchaseContent,
  SubscriptionCheckout,
  SubscriptionPurchaseContent,
  SubscriptionPurchaseFooter,
  SubscriptionPurchaseHeader,
} from './subscription-checkout';
import { useFetchFlexibleSlotsPricesQuery } from './product-description.hooks';
import { useV2Router } from '../dashboard/hooks/use-v2-router';
import useShowOrganisationQuery from '../organisations/hooks/use-show-organisation-query';
import { atom, useSetAtom } from 'jotai';

const Wrapper = tw.div`bg-neutral-10`;
const PageWrapper = styled(EmployerPageContentWrapper)`
  ${tw`py-[40px]`}
`;
const StyledPurchaseContent = styled.div`
  ${tw`h-[90vh] relative overflow-y-auto px-10 w-full lg:(-top-10 pb-10 h-[85vh]) xl:h-[83vh]`};

  /* scroll styles */
  /* For WebKit (Safari, Chrome, Opera) */
  ::-webkit-scrollbar {
    ${tw`w-1`}/* Width of the scrollbar */
  }
  ::-webkit-scrollbar-thumb {
    background-color: ${theme`colors.neutral.40`}; /* Color of the thumb */
    ${tw`rounded`}/* Rounded corners */
  }
  /* For Firefox */
  scrollbar-width: thin; /* "thin" or "auto" */
  scrollbar-color: ${theme`colors.neutral.40`} transparent;
  /* For MS Edge */
  &::-ms-scrollbar {
    ${tw`w-1`}
  }
  &::-ms-scrollbar-thumb {
    background-color: ${theme`colors.neutral.40`}; /* Color of the thumb */
    ${tw`rounded`}/* Rounded corners */
  }
`;

export const globalDiscountAtom = atom<{ coupon: string; discount: number }>({ coupon: '', discount: 0 });

const StickySubscriptionCheckoutBundles = () => {
  return (
    <div tw="hidden lg:block relative w-full lg:(min-w-[420px]) xl:(max-w-[420px] min-w-[500px])">
      <div tw="sticky top-0 border-l border-neutral-40 h-screen">
        <div tw="inline-flex pt-10 flex-col gap-10 justify-start items-start w-full h-full">
          <StyledPurchaseContent>
            <div tw="mb-10 pt-10">
              <SubscriptionPurchaseHeader />
            </div>
            <SubscriptionPurchaseContent />
          </StyledPurchaseContent>
        </div>
        <div tw="sticky bottom-0 z-10 min-w-full">
          <div tw="absolute -top-2 left-0 w-full h-4 pointer-events-none shadow-[0_-1px_16px_0_rgba(0,0,26,0.25)] z-0 bg-neutral-10" />
          <div tw="relative px-10 py-5 border-neutral-40 bg-neutral-10 z-10">
            <SubscriptionPurchaseFooter />
          </div>
        </div>
      </div>
    </div>
  );
};

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface IProductDescriptionProps {}
const ProductDescription: React.FunctionComponent<IProductDescriptionProps> = (props) => {
  const { t } = useTranslation('product-description-page');
  const { trackEvent } = useAnalytics();
  const { user } = useAuth();
  const { showMessages } = useIntercom();
  const router = useV2Router();
  const isFreemiumUser = useIsFreemiumUser();
  const setGlobalDiscount = useSetAtom(globalDiscountAtom);

  useShowOrganisationQuery({
    context: 'employer_purchase_page',
    staleTime: Infinity,
  });

  React.useEffect(() => {
    if (router?.query?.coupon === 'PRO20') setGlobalDiscount({ coupon: 'PRO20', discount: 100 });
  }, [router?.query, setGlobalDiscount]);

  const contactSupportButton = (
    <ButtonV2
      variant="tertiary"
      StartIcon={HeadphoneIcon}
      onClick={() => {
        // open Calendly in new tab
        trackEvent('employer_click_support', {
          source_page: 'product-description-desk',
          email: user?.email,
          phone: user?.phone_number,
        });
        showMessages();
      }}
    >
      {t('product_description_contact_support')}
    </ButtonV2>
  );

  useFetchFlexibleSlotsPricesQuery();
  return (
    <Wrapper>
      <EmployerSecondaryLayout hideNavigation title={t('product_description_title')}>
        <div tw="relative inline-flex w-full">
          <PageWrapper>
            <EmployerPrimaryLayoutHeader
              goToPreviousPage={() =>
                router.push(isFreemiumUser ? '/employer/homepage' : '/employer/payments-and-invoices')
              }
              customHeaderStyles={tw`pb-16 pt-0 flex justify-between flex-row`}
              showInfoIcon={false}
              customRightElement={contactSupportButton}
            />
            <div tw="flex flex-col gap-20">
              <HeaderSection />
              <FlexibleSlotPurchaseContent />
              <TrustedCompaniesSection />
              <HowItWorksStepsSection />
              <ActivateFirstSlotBanner />
              <HowItWorksSection />
              <FaqSection />
              <ActivateFirstSlotBanner />
              {/* <FlexibleSlotSection /> */}
              {/* <FunctionalitiesSection />
              <ActivateFirstSlotBanner />
              <AdvantagesSection />
              <IncludedFeaturesSection />
              <ActivateFirstSlotBanner /> */}
              <ContactSection />
            </div>
          </PageWrapper>
          {/* <StickySubscriptionCheckoutBundles /> */}
        </div>
      </EmployerSecondaryLayout>
      <SubscriptionCheckout />
    </Wrapper>
  );
};

export default ProductDescription;
